import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type FlashType = "success" | "error" | "info";

export interface FlashMessage {
  id: number;
  type: FlashType;
  message: string;
}

interface FlashContextValue {
  flashes: FlashMessage[];
  push: (message: string, type?: FlashType) => void;
  dismiss: (id: number) => void;
}

const FlashContext = createContext<FlashContextValue | null>(null);

/** Matches the legacy 4s auto-dismiss in script.js. */
const AUTO_DISMISS_MS = 4000;
const EXIT_ANIMATION_MS = 220;

export function FlashProvider({ children }: { children: ReactNode }) {
  const [flashes, setFlashes] = useState<FlashMessage[]>([]);
  const [hiding, setHiding] = useState<number[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const remove = useCallback((id: number) => {
    setFlashes((current) => current.filter((flash) => flash.id !== id));
    setHiding((current) => current.filter((hidingId) => hidingId !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      setHiding((current) => (current.includes(id) ? current : [...current, id]));
      const timer = setTimeout(() => remove(id), EXIT_ANIMATION_MS);
      timers.current.set(id, timer);
    },
    [remove],
  );

  const push = useCallback(
    (message: string, type: FlashType = "info") => {
      const id = nextId.current++;
      setFlashes((current) => [...current, { id, type, message }]);
      const timer = setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  const value = useMemo<FlashContextValue>(
    () => ({ flashes, push, dismiss }),
    [flashes, push, dismiss],
  );

  return (
    <FlashContext.Provider value={value}>
      {children}
      <FlashStack hiding={hiding} />
    </FlashContext.Provider>
  );
}

function FlashStack({ hiding }: { hiding: number[] }) {
  const context = useContext(FlashContext);
  if (!context || context.flashes.length === 0) return null;

  return (
    <div className="z-flash-stack" role="status" aria-live="polite">
      {context.flashes.map((flash) => (
        <div
          key={flash.id}
          className={[
            "z-flash",
            `z-flash--${flash.type}`,
            hiding.includes(flash.id) ? "z-flash--hiding" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => context.dismiss(flash.id)}
        >
          {flash.message}
        </div>
      ))}
    </div>
  );
}

export function useFlash(): FlashContextValue {
  const context = useContext(FlashContext);
  if (!context) {
    throw new Error("useFlash must be used inside a FlashProvider");
  }
  return context;
}
